/**
 * rAF-driven scroll: `scroller.scrollTop` from its current value to `to` over
 * `durationMs` on the cubic-bezier(0.36, 0.66, 0.04, 1) curve (KB_MOTION's
 * curve, solved exactly with Newton + bisection). Unlike native
 * `scrollTo({ behavior: 'smooth' })` its duration is ours, so a caller's
 * end-of-motion timer lands exactly when the scroll does. Clock = Date.now.
 * Returns a cancel function (idempotent; leaves scrollTop where it is).
 */
const X1 = 0.36; const Y1 = 0.66; const X2 = 0.04; const Y2 = 1;

const bez = (t, a, b) => 3 * (1 - t) * (1 - t) * t * a + 3 * (1 - t) * t * t * b + t * t * t;

export function easeKbMotion(x) {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  let t = x;
  for (let i = 0; i < 8; i += 1) {
    const err = bez(t, X1, X2) - x;
    if (Math.abs(err) < 1e-5) return bez(t, Y1, Y2);
    const d = 3 * (1 - t) * (1 - t) * X1 + 6 * (1 - t) * t * (X2 - X1) + 3 * t * t * (1 - X2);
    if (Math.abs(d) < 1e-6) break;
    t -= err / d;
  }
  let lo = 0; let hi = 1; t = x;
  for (let i = 0; i < 24; i += 1) {
    if (bez(t, X1, X2) < x) lo = t; else hi = t;
    t = (lo + hi) / 2;
  }
  return bez(t, Y1, Y2);
}

export function rafScrollTo(scroller, to, durationMs) {
  const from = scroller.scrollTop;
  const start = Date.now();
  let raf = 0;
  const step = () => {
    const p = Math.min(1, (Date.now() - start) / durationMs);
    scroller.scrollTop = from + (to - from) * easeKbMotion(p);
    raf = p < 1 ? requestAnimationFrame(step) : 0;
  };
  raf = requestAnimationFrame(step);
  return () => { if (raf) cancelAnimationFrame(raf); raf = 0; };
}
